import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { SearchInput } from 'src/components/Forms/FormInputs/SearchInput';
import { MetricsDropdown } from 'src/components/Forms/MetricsDropdown';
import { Page } from 'src/components/Page';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useMetrics } from 'src/hooks/useMetrics';
import { Wrapper } from 'src/pages/ConnectorMetrics/styled';
import { groupMetricsByCategory, isConnectorType } from 'src/utils';
import { ConnectorType } from 'src/utils/gql/types';
import { useMutation, useQuery } from '@apollo/client';
import {
  SAVE_DB_CONNECTOR_CONFIG,
  GET_DB_CONNECTOR_CONFIG,
} from 'src/utils/gql/queries';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';

export const ConnectorMetrics: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const connectorTypeParam = searchParams.get('type');
  const type = isConnectorType(connectorTypeParam)
    ? connectorTypeParam
    : ConnectorType.FACEBOOK;
  const { loading, metrics, connectorDetails } = useMetrics({ type });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const metricsContainerRef = useRef<HTMLDivElement | null>(null);
  const collapseRef = useRef<HTMLDivElement | null>(null);

  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState('');

  const toggleCollapse = () => setExpanded(!expanded);

  const handleClickBack = () => navigate(`/${ROUTE_SOURCES.CONNECTORS}`);

  const handleDelete = () => setSearch('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearch(value);
  };

  // temporary code

  const preFilteredMetrics = useMemo(
    () =>
      type === ConnectorType.GOOGLE_ADS
        ? [...metrics].sort((a, b) => a.label.localeCompare(b.label))
        : metrics,
    [metrics, type],
  );
  /// /

  const filteredMetrics = useMemo(
    () =>
      preFilteredMetrics.filter((metric) =>
        metric.label.toLowerCase().includes(search.toLowerCase()),
      ),
    [preFilteredMetrics, search],
  );

  const metricsByCategory = groupMetricsByCategory(filteredMetrics);

  // DB config tools
  const { databaseConnectorId } = useConnectors();
  const { data: dbCfg } = useQuery(GET_DB_CONNECTOR_CONFIG, {
    variables: { connectorId: databaseConnectorId || '' },
    skip: !databaseConnectorId || type !== ConnectorType.DATABASE,
    fetchPolicy: 'network-only',
  });

  const [saveDbConfig, { loading: savingDb }] = useMutation(
    SAVE_DB_CONNECTOR_CONFIG,
  );

  const handleSaveDbConfig = async () => {
    if (type !== ConnectorType.DATABASE || !databaseConnectorId) return;
    const keys = filteredMetrics.map((m) => m.key);
    await saveDbConfig({
      variables: {
        input: {
          connectorId: databaseConnectorId,
          selectedMetrics: keys,
          selectedSplits:
            dbCfg?.getDatabaseConnectorConfig?.selectedSplits || [],
          selectedTables:
            dbCfg?.getDatabaseConnectorConfig?.selectedTables || [],
          dateColumn:
            dbCfg?.getDatabaseConnectorConfig?.dateColumn || undefined,
          joins: dbCfg?.getDatabaseConnectorConfig?.joins || [],
        },
      },
    });
  };

  useEffect(() => {
    const adjustCollapseContentHeights = () => {
      if (collapseRef.current && metricsContainerRef.current) {
        const containerHeight = metricsContainerRef.current.clientHeight;
        const closedCollapseHeight = 34;
        collapseRef.current.style.maxHeight = `${Math.max(containerHeight - closedCollapseHeight)}px`;
      }
    };

    if (!isSmallScreen) {
      adjustCollapseContentHeights();
    }
  }, [isSmallScreen]);

  return (
    <Page>
      <Wrapper>
        <Typography variant="h3">Metrics</Typography>
        <SearchInput
          placeholder="Find a metric"
          name="search"
          value={search}
          onDelete={handleDelete}
          onChange={handleChange}
        />
        <Box flexGrow={1} ref={metricsContainerRef} mt="20px">
          <MetricsDropdown
            type={type}
            label={connectorDetails.title}
            icon={connectorDetails.icon}
            expanded={expanded}
            metricsByCategory={metricsByCategory}
            loading={loading}
            onToggle={toggleCollapse}
            ref={collapseRef}
          />
        </Box>

        {/* DB config saving removed per UX decision; use the wizard instead */}
        <Button fullWidth variant="contained" onClick={handleClickBack}>
          Back
        </Button>
      </Wrapper>
    </Page>
  );
};
